import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's default icon path issues with Webpack/Vite
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: iconRetina,
    iconUrl: icon,
    shadowUrl: iconShadow,
});

// Helper component to add a click listener to the map
function MapClickHandler({ onLocationSelect }) {
    useMapEvents({
        click(e) {
            onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
}

export default function LocationSearch({
    value,
    lat,
    lng,
    onChange,
    placeholder = "Adresse suchen...",
    showMapToggle = true
}) {
    const [query, setQuery] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [loading, setLoading] = useState(false);
    const timeoutRef = useRef(null);

    // Sync external value changes
    useEffect(() => {
        if (value !== undefined && value !== query) {
            setQuery(value || '');
        }
    }, [value]);

    const fetchSuggestions = async (searchQuery) => {
        if (!searchQuery || searchQuery.length < 3) {
            setSuggestions([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5&countrycodes=de,at,ch`);
            const data = await res.json();
            setSuggestions(data || []);
            setShowSuggestions(true);
        } catch (err) {
            console.error('Nominatim error', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInput = (e) => {
        const val = e.target.value;
        setQuery(val);
        // Inform parent that text changed but maybe no exact lat/lng yet (unless they clear)
        if (val === '') {
            onChange({ address: '', lat: null, lng: null });
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            fetchSuggestions(val);
        }, 500); // 500ms debounce
    };

    const handleSelectSuggestion = (item) => {
        const selectedAddress = item.display_name;
        const selectedLat = parseFloat(item.lat);
        const selectedLng = parseFloat(item.lon);

        setQuery(selectedAddress);
        setShowSuggestions(false);
        setSuggestions([]);

        onChange({ address: selectedAddress, lat: selectedLat, lng: selectedLng });
    };

    const handleMapClick = async ({ lat, lng }) => {
        // Reverse geocoding optional, otherwise just use coordinates
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
            const data = await res.json();
            const addr = data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
            setQuery(addr);
            onChange({ address: addr, lat, lng });
            setShowMap(false); // Auto close map after picking? Optional.
        } catch (err) {
            const addr = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
            setQuery(addr);
            onChange({ address: addr, lat, lng });
        }
    };

    const defaultCenter = [lat || 51.1657, lng || 10.4515]; // Germany center default
    const zoom = lat && lng ? 14 : 6;

    return (
        <div className="location-search-container">
            <div className="location-search-input-wrapper">
                <input
                    type="text"
                    className="location-search-input"
                    value={query}
                    onChange={handleInput}
                    onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder={placeholder}
                />

                {showMapToggle && (
                    <button
                        type="button"
                        className="location-search-map-btn"
                        onClick={() => setShowMap(!showMap)}
                        title="Auf Karte auswählen"
                    >
                        📍
                    </button>
                )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <ul style={{ position: 'absolute', zIndex: 60, top: '100%', left: 0, marginTop: '4px', width: '100%', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', maxHeight: '240px', overflowY: 'auto', listStyle: 'none', padding: 0 }}>
                    {suggestions.map((item, idx) => (
                        <li
                            key={item.place_id || idx}
                            onClick={() => handleSelectSuggestion(item)}
                            style={{ padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            title={item.display_name}
                        >
                            {item.display_name}
                        </li>
                    ))}
                </ul>
            )}

            {/* Loading Indicator */}
            {loading && <div style={{ position: 'absolute', right: showMapToggle ? '40px' : '8px', top: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>Suche...</div>}

            {/* Mini Map */}
            {showMap && (
                <div style={{ marginTop: '8px', height: '240px', width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden', zIndex: 50, position: 'relative' }}>
                    <MapContainer center={defaultCenter} zoom={zoom} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                        />
                        {lat && lng && (
                            <Marker position={[lat, lng]} />
                        )}
                        <MapClickHandler onLocationSelect={handleMapClick} />
                    </MapContainer>
                    <div style={{ position: 'absolute', bottom: '8px', left: '8px', zIndex: 400, backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '4px 8px', fontSize: '0.75rem', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', pointerEvents: 'none', color: '#374151' }}>
                        Klicke auf die Karte, um den Ort zu setzen
                    </div>
                </div>
            )}
        </div>
    );
}
